import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { useFormik } from "formik";
import * as Yup from "yup";

import api from "../../../service/api";

import { IPermissionUser } from "../../../@types";
import { CreateAndDetailLayout } from "../../../components/CreateAndDetailLayout";
import { ButtonCheck } from "../../../components/Form/ButtonCheck";
import { GroupInput } from "../../../components/Form/GroupInput";
import { Input } from "../../../components/Form/Input";
import { InputCheckboxV2 } from "../../../components/Form/InputCheckboxV2";
import { LayoutForm } from "../../../components/Form/LayoutForm";
import { HistoryAccess } from "../../../components/HistoryAccess";
import { PanelAndDetailAndCreateHeader } from "../../../components/panel/PanelAndDetailAndCreateHeader";
import { Button } from "../../../components/ui/button";
import { useBrands } from "../../../hook/queries/useBrands";
import { setFormikValues } from "../../../utils/setFormikValues";

interface HomeProps {
  location: {
    state: {
      user: IProvider;
    };
  };

  match: {
    params: {
      id: number;
    };
  };

  permission: IPermissionUser;
}

interface IProvider {
  id?: string;
  cod?: string;
  fullname?: string;
  phone?: string;
  isShippingCompany: boolean;
  isBrand: boolean;

  access?: {
    date: Date;
    dateToString: string;
  }[];
  brand?: {
    cod: number;
    name: string;
  };
  brands?: {
    id: number;
    cod: number;
    name: string;
  }[];
  email?: string;
}

const Provider: React.FC<HomeProps> = ({ permission, match }) => {
  const { addToast } = useToasts();
  const history = useHistory();

  const getBrands = useBrands(1, { active: 1 }, 9999);

  const [contentPage, setContentPage] = useState<IProvider>();

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (match.params.id) {
      (async () => {
        const getProvider = await api.get<IProvider>(
          `/provider/${match.params.id}`
        );

        setContentPage({
          ...getProvider.data,
          access: getProvider?.data?.access?.map((item) => ({
            ...item,
            dateToString: new Date(item.date).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            }),
          })),
        });
        setLoading(false);
      })();
    }
  }, [match.params.id]);

  useEffect(() => {
    if (contentPage) {
      setFormikValues(
        {
          ...contentPage,
          brands: contentPage.brands?.map((brand) => String(brand.id)),
        },
        formik
      );
    }
    // eslint-disable-next-line
  }, [contentPage]);

  const formik = useFormik({
    initialValues: {
      id: undefined,
      cod: undefined,
      email: undefined,
      fullname: undefined,
      phone: undefined,
      isShippingCompany: undefined,
      isBrand: undefined,
      brands: [],
      password: "",
    },
    validationSchema: Yup.object({
      fullname: Yup.string().required("Nome completo é obrigatório"),
      phone: Yup.string().required("Telefone é obrigatório"),
      email: Yup.string()
        .email("informe um e-mail válido")
        .required("email é obrigatório"),
      password: Yup.string()
        .min(8, "A senha precisa conter no mínimo 8 caracteres.")
        .matches(
          /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
          "A senha precisa conter letrar e numeros, no mínimo 8 caracteres e um caracter especial."
        ),
    }),
    onSubmit: (data) => handleSubmit(data as any),
  });

  async function handleSubmit(updates: IProvider) {
    setIsLoading(true);

    try {
      await api.put(`/provider/${updates.id}`, { ...updates });
      addToast("Fornecedor alterado com sucesso", {
        appearance: "success",
        autoDismiss: true,
      });
      history.push("/admin/fornecedores");
    } catch (err) {
      const error = err as any;
      if (!error.response) {
        return addToast(
          "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (error.response.data.error === "email already exists") {
        return addToast("Este e-mail já existe em nossa base de dados", {
          appearance: "warning",
          autoDismiss: true,
        });
      }
      if (error.response.data.error === "cod already exists") {
        return addToast("Este Código ERP já existe em nossa base de dados", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      if (error.response.status === 401) {
        return addToast("Sem permissão para esta ação", {
          appearance: "warning",
          autoDismiss: true,
        });
      }

      addToast(
        "Desculpe, ocorreu um erro interno, Tente novamente mais tarde",
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <CreateAndDetailLayout isLoading={loading}>
      <PanelAndDetailAndCreateHeader title={"Fornecedor"} goBack />

      <LayoutForm
        onSubmit={
          permission && permission.provider && permission.provider > 2
            ? formik.handleSubmit
            : () => {}
        }
      >
        <Input
          label="ID"
          {...formik.getFieldProps("id")}
          readOnly
          error={
            formik.touched.cod && formik.errors.cod
              ? formik.errors.cod
              : undefined
          }
        />

        <Input
          label="Código ERP"
          {...formik.getFieldProps("cod")}
          error={
            formik.touched.cod && formik.errors.cod
              ? formik.errors.cod
              : undefined
          }
        />

        <Input
          label="Nome"
          {...formik.getFieldProps("fullname")}
          error={
            formik.touched.fullname && formik.errors.fullname
              ? formik.errors.fullname
              : undefined
          }
        />

        <ButtonCheck
          label="É transportadora"
          bgColor="#11111d"
          name="status"
          value={formik.values.isShippingCompany}
          setValue={(data) => {
            formik.setFieldValue("isShippingCompany", data);
          }}
          options={[
            {
              value: true,
              field: "SIM",
            },
            {
              value: false,
              field: "NÃO",
            },
          ]}
          error={
            formik.touched.isShippingCompany && formik.errors.isShippingCompany
              ? formik.errors.isShippingCompany
              : undefined
          }
        />

        <ButtonCheck
          label="É marca"
          bgColor="#11111d"
          name="status"
          value={formik.values.isBrand}
          setValue={(data) => {
            formik.setFieldValue("isBrand", data);
          }}
          options={[
            {
              value: true,
              field: "SIM",
            },
            {
              value: false,
              field: "NÃO",
            },
          ]}
          error={
            formik.touched.isBrand && formik.errors.isBrand
              ? formik.errors.isBrand
              : undefined
          }
        />

        <GroupInput>
          <Input
            label="Email"
            {...formik.getFieldProps("email")}
            error={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : undefined
            }
          />
          <Input
            label="Telefone"
            {...formik.getFieldProps("phone")}
            error={
              formik.touched.phone && formik.errors.phone
                ? formik.errors.phone
                : undefined
            }
          />
        </GroupInput>

        <InputCheckboxV2
          label="Marcas"
          checks={formik.values.brands}
          data={
            getBrands?.data?.map((brand) => ({
              description: brand.name,
              value: brand.id.toString(),
            })) ?? []
          }
          name="brands"
          onChange={(e) => {
            formik.setFieldValue("brands", e);
          }}
          error={
            formik.touched.brands && formik.errors.brands
              ? formik.errors.brands.toString()
              : undefined
          }
        />

        <h3>Alterar a senha</h3>
        <Input
          label="Senha"
          {...formik.getFieldProps("password")}
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : undefined
          }
        />

        {permission && permission.provider && permission.provider > 2 && (
          <Button type="submit" isLoading={isLoading}>
            Alterar
          </Button>
        )}
      </LayoutForm>

      <HistoryAccess
        data={
          contentPage?.access
            ? contentPage.access.map((acces) => ({
                id: String(contentPage.id),
                cod: String(contentPage.cod),
                name: contentPage.fullname ?? "",
                date: acces.date,
                dateToString: acces.dateToString,
              }))
            : []
        }
      />
    </CreateAndDetailLayout>
  );
};

export default Provider;
